import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserCog,
  ScanEye,
  ShieldAlert,
  Activity,
  Flame,
  HeartPulse,
  Megaphone,
} from "lucide-react";

const SEV_COLOR = {
  critical: "hsl(var(--destructive))",
  high:     "hsl(var(--warning))",
  medium:   "hsl(var(--primary))",
  low:      "hsl(var(--muted-foreground))",
};

const titleCase = (s) =>
  String(s || "").split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const relTime = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const classify = (ev) => {
  const t = ev.type || "";
  if (t.includes("vip") || ev.title?.toLowerCase().includes("vip")) {
    return { cat: "VIP", icon: UserCog, color: "hsl(var(--primary))", route: "/vip" };
  }
  if (t.includes("fire") || t.includes("smoke")) {
    return { cat: "Safety", icon: Flame, color: "hsl(var(--destructive))", route: "/incidents" };
  }
  if (t.includes("fall") || t.includes("medical")) {
    return { cat: "Medical", icon: HeartPulse, color: "hsl(var(--destructive))", route: "/incidents" };
  }
  if (t.includes("intrusion") || t.includes("fence") || t.includes("fight") || t.includes("abandoned_bag")) {
    return { cat: "Security", icon: ShieldAlert, color: "hsl(var(--warning))", route: "/incidents" };
  }
  if (t.includes("crowd") || t.includes("stampede") || t.includes("congestion") || t.includes("queue")) {
    return { cat: "Crowd", icon: Activity, color: "hsl(var(--primary))", route: "/crowd" };
  }
  return { cat: "Detection", icon: ScanEye, color: "hsl(217 91% 60%)", route: "/detection" };
};

// Pixels-per-second scroll speed. Lower = slower.
const SPEED_PX_PER_SEC = 35;

export const EventMarquee = ({ alerts = [], max = 18 }) => {
  const navigate = useNavigate();

  // Most recent, non-resolved events first
  const items = useMemo(() => {
    return (alerts || [])
      .filter((a) => a.status !== "resolved")
      .slice(0, max);
  }, [alerts, max]);

  const trackRef  = useRef(null);
  const posRef    = useRef(0);          // current X offset (negative as it scrolls left)
  const hoverRef  = useRef(false);

  // Pause on hover via ref so animation loop reads latest value without re-binding.
  const [, force] = useState(0); void force;  // keep linter happy if we ever need to re-render

  useEffect(() => {
    let raf;
    let last = performance.now();

    const step = (now) => {
      const dt = (now - last) / 1000;
      last = now;

      if (!hoverRef.current && trackRef.current) {
        // The track contains 2 duplicated halves; one half-width loops back to 0.
        const halfWidth = trackRef.current.scrollWidth / 2;
        if (halfWidth > 0) {
          posRef.current -= SPEED_PX_PER_SEC * dt;
          if (Math.abs(posRef.current) >= halfWidth) {
            posRef.current += halfWidth; // seamless wrap
          }
          trackRef.current.style.transform = `translateX(${posRef.current}px)`;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  // If the items list shrinks dramatically (or empties), reset position so we
  // don't end up stranded past the new content's width.
  useEffect(() => {
    if (!trackRef.current) return;
    const halfWidth = trackRef.current.scrollWidth / 2;
    if (halfWidth === 0 || Math.abs(posRef.current) > halfWidth) {
      posRef.current = 0;
      trackRef.current.style.transform = "translateX(0px)";
    }
  }, [items]);

  const empty = items.length === 0;

  return (
    <div
      className="h-7 border-b border-border flex items-stretch shrink-0 overflow-hidden bg-gradient-to-r from-[hsl(var(--primary)/0.10)] via-[hsl(var(--primary)/0.04)] to-[hsl(var(--surface))]"
      data-testid="event-marquee"
    >
      <div
        className="shrink-0 px-2 flex items-center gap-1.5 border-r border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary))]"
        title="Live events feed"
      >
        <Megaphone className="w-3 h-3 text-[hsl(var(--primary-foreground))]" strokeWidth={2} />
        <span className="font-display font-black text-[10px] text-[hsl(var(--primary-foreground))]">LIVE</span>
      </div>

      <div
        className="relative flex-1 overflow-hidden"
        onMouseEnter={() => { hoverRef.current = true; }}
        onMouseLeave={() => { hoverRef.current = false; }}
      >
        {empty ? (
          <div className="absolute inset-0 flex items-center px-3 text-[10px] text-muted-foreground" data-testid="event-marquee-empty">
            No live events. Standing by…
          </div>
        ) : (
          <div
            ref={trackRef}
            className="absolute inset-y-0 left-0 flex items-center whitespace-nowrap gap-6 will-change-transform"
            style={{ transform: "translateX(0px)" }}
          >
            {[0, 1].map((dup) => (
              <div key={dup} className="flex items-center gap-6 shrink-0" aria-hidden={dup === 1}>
                {items.map((ev) => {
                  const meta = classify(ev);
                  const Icon = meta.icon;
                  const sevColor = SEV_COLOR[ev.severity] || meta.color;
                  return (
                    <button
                      key={`${dup}-${ev.id}`}
                      onClick={() => navigate(meta.route)}
                      data-testid={dup === 0 ? `marquee-item-${ev.id}` : undefined}
                      className="inline-flex items-center gap-1.5 text-[11px] hover:underline focus:outline-none"
                      title={`${ev.title} · ${ev.zone_name}`}
                    >
                      <Icon className="w-3 h-3 shrink-0" style={{ color: meta.color }} />
                      <span
                        className="px-1 text-[9px] font-bold tracking-wide border"
                        style={{ color: meta.color, borderColor: meta.color }}
                      >
                        {meta.cat}
                      </span>
                      <span
                        className="px-1 text-[9px] font-bold tracking-wide border"
                        style={{ color: sevColor, borderColor: sevColor }}
                      >
                        {titleCase(ev.severity)}
                      </span>
                      <span className="text-foreground font-medium">{ev.title}</span>
                      <span className="text-muted-foreground">· {ev.zone_name}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">· {relTime(ev.ts)}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventMarquee;
